import { useEffect, useMemo, useRef, useState } from "react";
import {
  createAdminConstructorVideo,
  createAdminPracticeVideo,
  createAdminLiveVideo,
  deleteAdminConstructorVideo,
  deleteAdminPracticeVideo,
  deleteAdminLiveVideo,
  getAdminConstructorVideos,
  getAdminPracticeVideos,
  getAdminLiveVideos,
  getVideoAdminMeta,
  updateAdminConstructorVideo,
  updateAdminPracticeVideo,
  updateAdminLiveVideo,
  deleteCloudinaryImage,
} from "../../services/adminService";
import { useAppContext } from "../../context/AppContext";
import PopupModal from "../utilPages/PopupModal";
import VideoDisplay from "../utilPages/VideoDisplay";
import ActionToggleButton from "../utilPages/ActionToggleButton";
import CloudinaryImageUpload from "../utilPages/CloudinaryImageUpload";
import { cloudinaryPublicId } from "../../utils/cloudinaryUtils";

const emptyForm = {
  duration: "",
  tags: "",
  level: "",
  type: "",
  group: "",
  scope: "",
  contentType: "",
  isTrial: false,
  hidden: false,
  coverPhotoRu: "",
  coverPhotoEn: "",
  headerRu: "",
  headerEn: "",
  descriptionRu: "",
  descriptionEn: "",
  videoRuDefault: "",
  videoRuAlternative: "",
  videoEnDefault: "",
  videoEnAlternative: "",
  liveDate: "",
};

const mapVideoToForm = (video) => ({
    duration: video.duration ?? "",
    tags: Array.isArray(video.tags) ? video.tags.join(", ") : "",
    level: Array.isArray(video.level) ? video.level.join(", ") : "",
    type: Array.isArray(video.type) ? video.type.join(", ") : "",
    group: video.group || "",
    scope: video.scope || "",
    contentType: video.contentType || "",
    isTrial: video.isTrial === true,
    hidden: video.hidden === true,
    coverPhotoRu: video.coverPhotos?.ru || "",
    coverPhotoEn: video.coverPhotos?.en || "",
    headerRu: video.header?.ru || "",
    headerEn: video.header?.en || "",
    descriptionRu: video.description?.ru || "",
    descriptionEn: video.description?.en || "",
    videoRuDefault: video.videos?.ru?.default || video.videoURL || "",
    videoRuAlternative: video.videos?.ru?.alternative || video.alternativeVideoURL || "",
    videoEnDefault: video.videos?.en?.default || "",
    videoEnAlternative: video.videos?.en?.alternative || "",
    liveDate: toDatetimeLocalValue(video.liveDate || video.createdAt),
  });

const splitList = (value) => {
  if (!value) return [];
  return value
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
};

const toDatetimeLocalValue = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const TAG_OPTIONS = [
  "balance",
  "lotus",
  "hipOpenners",
  "shavasana",
  "breathing",
  "legStrength",
  "armStrength",
  "flexibility",
  "backStrength",
  "handstands",
  "core",
  "armBalances",
  "backBends",
  "splits",
  "twists",
  "surya",
  "shoulders",
  "chestOpeners",
  "seated",
  "laying",
  "noInversions",
  "glutes",
];

const MultiSelectDropdown = ({
  label,
  options,
  selected,
  onToggle,
  placeholder = "Select…",
}) => {
  const [open, setOpen] = useState(false);
  const selectedLabel = selected.length ? selected.join(", ") : placeholder;

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-left bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brown focus:border-brown"
        aria-expanded={open}
      >
        <span className={selected.length ? "text-gray-900" : "text-gray-500"}>
          {selectedLabel}
        </span>
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg max-h-56 overflow-y-auto">
          <div className="p-2 space-y-1">
            {(options || []).map((opt) => {
              const isSelected = selected.includes(opt);
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => onToggle(opt)}
                  className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-gray-50 text-sm"
                >
                  <span className="text-gray-800">{opt}</span>
                  <span
                    className={`inline-flex items-center justify-center w-5 h-5 rounded border ${
                      isSelected ? "bg-brown border-brown" : "border-gray-300"
                    }`}
                    aria-hidden
                  >
                    {isSelected && (
                      <svg
                        className="w-3.5 h-3.5 text-white"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.704 5.29a1 1 0 010 1.42l-7.5 7.5a1 1 0 01-1.42 0l-3.5-3.5a1 1 0 011.42-1.42l2.79 2.79 6.79-6.79a1 1 0 011.42 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </span>
                </button>
              );
            })}
            {(!options || options.length === 0) && (
              <p className="px-2 py-2 text-sm text-gray-500">No options</p>
            )}
          </div>
          <div className="border-t border-gray-200 p-2 flex justify-end">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-sm font-medium text-brown hover:underline"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminVideos = ({ token }) => {
  const idFromYoutubeUrl = (url) => {
    if (!url || typeof url !== "string") return "";
    try {
      const parsed = new URL(url);
      if (parsed.hostname.includes("youtu.be")) {
        return parsed.pathname.replace("/", "");
      }
      if (parsed.hostname.includes("youtube.com")) {
        if (parsed.pathname.includes("/embed/")) {
          return parsed.pathname.split("/embed/")[1]?.split("/")[0] || "";
        }
        return parsed.searchParams.get("v") || "";
      }
    } catch (_) {
      return "";
    }
    return "";
  };

  const idFromRutubeUrl = (url) => {
    if (!url || typeof url !== "string") return "";
    try {
      const parsed = new URL(url);
      if (!parsed.hostname.includes("rutube.ru")) return "";
      if (parsed.pathname.includes("/play/embed/")) return "";
      const match = parsed.pathname.match(/\/video\/([^/]+)/);
      return match ? match[1] : "";
    } catch {
      return "";
    }
  };

  const toEmbedUrl = (url) => {
    const ytId = idFromYoutubeUrl(url);
    if (ytId) return `https://www.youtube.com/embed/${ytId}`;
    const rtId = idFromRutubeUrl(url);
    if (rtId) return `https://rutube.ru/play/embed/${rtId}`;
    return url;
  };

  const normalizeVideoUrl = (field) => (e) => {
    const converted = toEmbedUrl(e.target.value.trim());
    if (converted !== e.target.value.trim()) {
      setForm((p) => ({ ...p, [field]: converted }));
    }
  };

  const getProvider = (url) => {
    if (!url || typeof url !== "string") return "unknown";
    try {
      const host = new URL(url).hostname;
      if (host.includes("youtube.com") || host.includes("youtu.be")) return "youtube";
      if (host.includes("rutube.ru")) return "rutube";
    } catch (_) {
      return "unknown";
    }
    return "unknown";
  };

  const getCoverPhoto = (item) =>
    item?.coverPhotos?.[language] ||
    item?.coverPhotos?.ru ||
    item?.coverPhotos?.en ||
    "";

  const { setToaster, language } = useAppContext();
  const [mode, setMode] = useState("practice");
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [visibilityFilter, setVisibilityFilter] = useState("visible");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [previewItem, setPreviewItem] = useState(null);
  const [useAlternativePlayer, setUseAlternativePlayer] = useState(false);
  const [matchedListMinHeight, setMatchedListMinHeight] = useState(null);
  const [langTab, setLangTab] = useState("ru");
  const formPanelRef = useRef(null);
  const listScrollRef = useRef(null);
  const pendingUploadsRef = useRef(new Set());

  useEffect(() => {
    if (!token) return;
    getVideoAdminMeta(token).then(setMeta).catch(() => setMeta(null));
  }, [token]);

  useEffect(() => {
    const element = formPanelRef.current;
    if (!element) return undefined;

    const updateHeight = () => {
      if (window.innerWidth < 1024) {
        setMatchedListMinHeight(null);
        return;
      }
      setMatchedListMinHeight(element.getBoundingClientRect().height);
    };

    updateHeight();

    const observer = new ResizeObserver(() => {
      updateHeight();
    });
    observer.observe(element);
    window.addEventListener("resize", updateHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateHeight);
    };
  }, [mode, editingId, form, items.length, loading]);

  const loadItems = async ({ preserveScroll = false, silent = false } = {}) => {
    if (!token) return;
    const previousScrollTop = preserveScroll ? listScrollRef.current?.scrollTop ?? 0 : 0;
    if (!silent) setLoading(true);
    try {
      const data = mode === "practice"
        ? await getAdminPracticeVideos(token)
        : mode === "constructor"
        ? await getAdminConstructorVideos(token)
        : await getAdminLiveVideos(token);
      setItems(Array.isArray(data) ? data : []);
      if (preserveScroll) {
        requestAnimationFrame(() => {
          if (listScrollRef.current) {
            listScrollRef.current.scrollTop = previousScrollTop;
          }
        });
      }
    } catch (error) {
      setToaster({ message: error.message || "Failed loading videos", type: "error", show: true });
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, [mode, token]);

  const options = useMemo(() => {
    if (!meta) return { levels: [], types: [], groups: [], scopes: [], contentTypes: [] };
    if (mode === "practice") return meta.practice || {};
    if (mode === "constructor") return meta.constructor || {};
    return meta.live || {};
  }, [meta, mode]);

  const handleEdit = (item) => {
    setEditingId(item._id);
    setForm(mapVideoToForm(item));
  };

  const discardPendingUploads = () => {
    pendingUploadsRef.current.forEach((url) => {
      deleteCloudinaryImage(token, cloudinaryPublicId(url)).catch(() => {});
    });
    pendingUploadsRef.current.clear();
  };

  const resetForm = () => {
    discardPendingUploads();
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleUploaded = ({ newUrl, replacedUrl }) => {
    if (replacedUrl) {
      pendingUploadsRef.current.delete(replacedUrl);
      deleteCloudinaryImage(token, cloudinaryPublicId(replacedUrl)).catch(() => {});
    }
    if (newUrl) {
      pendingUploadsRef.current.add(newUrl);
    }
  };

  const buildPayload = () => ({
    duration: form.duration === "" ? undefined : Number(form.duration),
    tags: splitList(form.tags),
    level: splitList(form.level),
    type: splitList(form.type),
    group: form.group || undefined,
    scope: mode === "practice" ? (form.scope || undefined) : undefined,
    contentType: mode === "practice" ? (form.contentType || undefined) : undefined,
    isTrial: mode === "constructor" ? form.isTrial : undefined,
    hidden: form.hidden === true,
    coverPhotos: mode === "practice" || mode === "live"
      ? {
          ru: form.coverPhotoRu || undefined,
          en: form.coverPhotoEn || undefined,
        }
      : undefined,
    header: mode === "practice"
      ? {
          ru: form.headerRu || "",
          en: form.headerEn || "",
        }
      : undefined,
    description: mode === "practice"
      ? {
          ru: form.descriptionRu || "",
          en: form.descriptionEn || "",
        }
      : undefined,
    videos: {
      ru: {
        default: form.videoRuDefault,
        alternative: form.videoRuAlternative || undefined,
      },
      en: form.videoEnDefault
        ? {
            default: form.videoEnDefault,
            alternative: form.videoEnAlternative || undefined,
          }
        : undefined,
    },
    liveDate: mode === "live" && editingId && form.liveDate
      ? new Date(form.liveDate).toISOString()
      : undefined,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.videoRuDefault.trim()) {
      setToaster({ message: "RU video link is required", type: "error", show: true });
      return;
    }
    if (mode === "practice" && !form.scope) {
      setToaster({ message: "Scope is required for practice videos", type: "error", show: true });
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      if (mode === "practice") {
        if (editingId) {
          await updateAdminPracticeVideo(token, editingId, payload);
        } else {
          await createAdminPracticeVideo(token, payload);
        }
      } else if (mode === "constructor") {
        if (editingId) {
          await updateAdminConstructorVideo(token, editingId, payload);
        } else {
          await createAdminConstructorVideo(token, payload);
        }
      } else {
        if (editingId) {
          await updateAdminLiveVideo(token, editingId, payload);
        } else {
          await createAdminLiveVideo(token, payload);
        }
      }
      setToaster({ message: editingId ? "Video updated" : "Video created", type: "success", show: true });
      pendingUploadsRef.current.clear();
      resetForm();
      loadItems({ preserveScroll: true, silent: true });
    } catch (error) {
      setToaster({ message: error.message || "Failed saving video", type: "error", show: true });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this video?")) return;
    try {
      if (mode === "practice") {
        await deleteAdminPracticeVideo(token, id);
      } else if (mode === "constructor") {
        await deleteAdminConstructorVideo(token, id);
      } else {
        await deleteAdminLiveVideo(token, id);
      }
      if (editingId === id) resetForm();
      setToaster({ message: "Video deleted", type: "success", show: true });
      loadItems({ preserveScroll: true, silent: true });
    } catch (error) {
      setToaster({ message: error.message || "Failed deleting video", type: "error", show: true });
    }
  };

  const getPrimaryUrl = (item) =>
    item?.videos?.[language]?.default ||
    item?.videos?.ru?.default ||
    item?.videos?.en?.default ||
    item?.videoURL ||
    "";

  const getAlternativeUrl = (item) =>
    item?.videos?.[language]?.alternative ||
    item?.videos?.ru?.alternative ||
    item?.videos?.en?.alternative ||
    item?.alternativeVideoURL ||
    "";

  const getYoutubeThumbUrl = (url) => {
    const id = idFromYoutubeUrl(url);
    if (!id) return "";
    return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
  };

  const getActivePreviewUrl = () => {
    if (!previewItem) return "";
    const primary = getPrimaryUrl(previewItem);
    const alternative = getAlternativeUrl(previewItem);
    return useAlternativePlayer ? (alternative || primary) : primary;
  };

  const openPreview = (item) => {
    const url = getPrimaryUrl(item);
    if (!url) {
      setToaster({ message: "No RU video available to preview", type: "info", show: true });
      return;
    }
    setUseAlternativePlayer(false);
    setPreviewItem(item);
  };

  const closePreview = () => {
    setPreviewItem(null);
    setUseAlternativePlayer(false);
  };

  const selectedTags = useMemo(() => splitList(form.tags), [form.tags]);
  const selectedLevels = useMemo(() => splitList(form.level), [form.level]);
  const selectedTypes = useMemo(() => splitList(form.type), [form.type]);
  const filteredItems = useMemo(() => {
    if (visibilityFilter === "hidden") {
      return items.filter((item) => item.hidden === true);
    }
    if (visibilityFilter === "visible") {
      return items.filter((item) => item.hidden !== true);
    }
    return items;
  }, [items, visibilityFilter]);

  const toggleTag = (tag) => {
    const nextTags = selectedTags.includes(tag)
      ? selectedTags.filter((t) => t !== tag)
      : [...selectedTags, tag];
    setForm((prev) => ({ ...prev, tags: nextTags.join(", ") }));
  };

  const toggleLevel = (level) => {
    const next = selectedLevels.includes(level)
      ? selectedLevels.filter((l) => l !== level)
      : [...selectedLevels, level];
    setForm((prev) => ({ ...prev, level: next.join(", ") }));
  };

  const toggleType = (type) => {
    const next = selectedTypes.includes(type)
      ? selectedTypes.filter((t) => t !== type)
      : [...selectedTypes, type];
    setForm((prev) => ({ ...prev, type: next.join(", ") }));
  };

  return (
    <div className="space-y-6">
      {/* Header with Mode Toggle */}
      <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Content Management</h2>
            <p className="text-sm text-gray-600 mt-1">Manage practice videos, constructor pieces, and live recordings</p>
          </div>
          <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
            <button
              type="button"
              onClick={() => {
                setMode("practice");
                resetForm();
              }}
              className={`px-4 py-2 text-sm font-medium rounded-md transition ${
                mode === "practice" ? "bg-brown text-white shadow-sm" : "text-gray-700 hover:text-brown"
              }`}
            >
              Practice Videos
            </button>
            <button
              type="button"
              onClick={() => {
                setMode("constructor");
                resetForm();
              }}
              className={`px-4 py-2 text-sm font-medium rounded-md transition ${
                mode === "constructor" ? "bg-brown text-white shadow-sm" : "text-gray-700 hover:text-brown"
              }`}
            >
              Constructor Pieces
            </button>
            <button
              type="button"
              onClick={() => {
                setMode("live");
                resetForm();
              }}
              className={`px-4 py-2 text-sm font-medium rounded-md transition ${
                mode === "live" ? "bg-brown text-white shadow-sm" : "text-gray-700 hover:text-brown"
              }`}
            >
              Lives
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 items-start lg:grid-cols-2 lg:items-stretch gap-6">
        {/* Form Section */}
        <div className="order-1">
          <div
            ref={formPanelRef}
            className="bg-white rounded-lg shadow p-4 sm:p-6 sticky top-6"
          >
            <h3 className="text-lg font-semibold text-gray-800 mb-1">
              {editingId ? "Edit Video" : "Add New Video"}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {mode === "practice" 
                ? "Add a new practice video to the library" 
                : mode === "constructor"
                ? "Add a new constructor piece for practice assembly"
                : "Add a new live recording"}
            </p>

            <form className="space-y-4" onSubmit={handleSubmit}>
              {/* Language Tab + Video Links */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Language</span>
                  <div className="inline-flex rounded-md border border-gray-200 bg-gray-50 p-0.5">
                    <button
                      type="button"
                      onClick={() => setLangTab("ru")}
                      className={`px-3 py-1 text-xs font-semibold rounded transition ${langTab === "ru" ? "bg-white shadow-sm text-gray-900" : "text-gray-500 hover:text-gray-700"}`}
                    >
                      RU
                    </button>
                    <button
                      type="button"
                      onClick={() => setLangTab("en")}
                      className={`px-3 py-1 text-xs font-semibold rounded transition ${langTab === "en" ? "bg-white shadow-sm text-gray-900" : "text-gray-500 hover:text-gray-700"}`}
                    >
                      EN
                    </button>
                  </div>
                </div>

                {langTab === "ru" ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Main Link <span className="text-red-500">*</span>
                      </label>
                      <input
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brown focus:border-brown"
                        placeholder="https://youtube.com/watch?v=... or youtu.be/..."
                        value={form.videoRuDefault}
                        onChange={(e) => setForm((p) => ({ ...p, videoRuDefault: e.target.value }))}
                        onBlur={normalizeVideoUrl("videoRuDefault")}
                      />
                    </div>
                    {/* <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Alternative Link</label>
                      <input
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brown focus:border-brown"
                        placeholder="Optional alternative"
                        value={form.videoRuAlternative}
                        onChange={(e) => setForm((p) => ({ ...p, videoRuAlternative: e.target.value }))}
                        onBlur={normalizeVideoUrl("videoRuAlternative")}
                      />
                    </div> */}
                  </>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Main Link</label>
                      <input
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brown focus:border-brown"
                        placeholder="Optional English version"
                        value={form.videoEnDefault}
                        onChange={(e) => setForm((p) => ({ ...p, videoEnDefault: e.target.value }))}
                        onBlur={normalizeVideoUrl("videoEnDefault")}
                      />
                    </div>
                    {/* <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Alternative Link</label>
                      <input
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brown focus:border-brown"
                        placeholder="Optional alternative EN"
                        value={form.videoEnAlternative}
                        onChange={(e) => setForm((p) => ({ ...p, videoEnAlternative: e.target.value }))}
                        onBlur={normalizeVideoUrl("videoEnAlternative")}
                      />
                    </div> */}
                  </>
                )}
              </div>

              {/* Basic Info Section */}
              {mode !== "live" && <div className="pt-3 border-t border-gray-200 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Duration (min)</label>
                    <input
                      type="number"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brown focus:border-brown"
                      placeholder="0"
                      value={form.duration}
                      onChange={(e) => setForm((p) => ({ ...p, duration: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Group</label>
                    <select
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brown focus:border-brown"
                      value={form.group}
                      onChange={(e) => setForm((p) => ({ ...p, group: e.target.value }))}
                    >
                      <option value="">Select group</option>
                      {(options.groups || []).map((g) => (
                        <option key={g} value={g}>
                          {g}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Tags</label>
                  <div className="flex flex-wrap gap-2 rounded-md border border-gray-200 bg-gray-50 p-2">
                    {TAG_OPTIONS.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => toggleTag(tag)}
                        className={`px-3 py-1 rounded-full text-xs border transition ${
                          selectedTags.includes(tag)
                            ? "bg-brown text-white border-brown"
                            : "bg-white text-gray-700 border-gray-300 hover:border-brown hover:text-brown"
                        }`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <MultiSelectDropdown
                    label="Levels"
                    options={options.levels || ["beginner", "experienced", "advanced"]}
                    selected={selectedLevels}
                    onToggle={toggleLevel}
                    placeholder="Any"
                  />
                  <MultiSelectDropdown
                    label="Types"
                    options={options.types || ["recovery", "progression"]}
                    selected={selectedTypes}
                    onToggle={toggleType}
                    placeholder="Any"
                  />
                </div>
              </div>}

              {/* Practice-Specific Fields */}
              {mode === "practice" && (
                <div className="pt-3 border-t border-gray-200 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Scope <span className="text-red-500">*</span>
                      </label>
                      <select
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brown focus:border-brown"
                        value={form.scope}
                        onChange={(e) => setForm((p) => ({ ...p, scope: e.target.value }))}
                      >
                        <option value="">Select scope</option>
                        {(options.scopes || []).map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Content Type</label>
                      <select
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brown focus:border-brown"
                        value={form.contentType}
                        onChange={(e) => setForm((p) => ({ ...p, contentType: e.target.value }))}
                      >
                        <option value="">Select type</option>
                        {(options.contentTypes || []).map((ct) => (
                          <option key={ct} value={ct}>
                            {ct}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {langTab === "ru" ? (
                    <CloudinaryImageUpload
                      label="Cover Photo"
                      value={form.coverPhotoRu}
                      onChange={(url) => setForm((p) => ({ ...p, coverPhotoRu: url }))}
                      onUploaded={handleUploaded}
                      token={token}
                    />
                  ) : (
                    <CloudinaryImageUpload
                      label="Cover Photo"
                      value={form.coverPhotoEn}
                      onChange={(url) => setForm((p) => ({ ...p, coverPhotoEn: url }))}
                      onUploaded={handleUploaded}
                      token={token}
                    />
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Video Header
                    </label>
                    <input
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brown focus:border-brown"
                      placeholder="Short title shown above the video"
                      value={langTab === "ru" ? form.headerRu : form.headerEn}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          [langTab === "ru" ? "headerRu" : "headerEn"]: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Description
                    </label>
                    <textarea
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brown focus:border-brown resize-y"
                      rows={4}
                      placeholder="Describe what this practice covers…"
                      value={langTab === "ru" ? form.descriptionRu : form.descriptionEn}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          [langTab === "ru" ? "descriptionRu" : "descriptionEn"]: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
              )}

              {/* Live-Specific Fields */}
              {mode === "live" && (
                <div className="pt-3 border-t border-gray-200 space-y-3">
                  {langTab === "ru" ? (
                    <CloudinaryImageUpload
                      label="Cover Photo"
                      value={form.coverPhotoRu}
                      onChange={(url) => setForm((p) => ({ ...p, coverPhotoRu: url }))}
                      onUploaded={handleUploaded}
                      token={token}
                    />
                  ) : (
                    <CloudinaryImageUpload
                      label="Cover Photo"
                      value={form.coverPhotoEn}
                      onChange={(url) => setForm((p) => ({ ...p, coverPhotoEn: url }))}
                      onUploaded={handleUploaded}
                      token={token}
                    />
                  )}
                  {editingId && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Live Date
                      </label>
                      <input
                        type="datetime-local"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-brown"
                        value={form.liveDate}
                        max={toDatetimeLocalValue(new Date().toISOString())}
                        onChange={(e) => setForm((p) => ({ ...p, liveDate: e.target.value }))}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Constructor-Specific Fields */}
              {mode === "constructor" && (
                <div className="pt-3 border-t border-gray-200 space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-brown border-gray-300 rounded focus:ring-brown"
                      checked={form.isTrial}
                      onChange={(e) => setForm((p) => ({ ...p, isTrial: e.target.checked }))}
                    />
                    Trial video (accessible without subscription)
                  </label>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-brown border-gray-300 rounded focus:ring-brown"
                      checked={form.hidden}
                      onChange={(e) => setForm((p) => ({ ...p, hidden: e.target.checked }))}
                    />
                    Hidden (only visible in dashboard admin)
                  </label>
                </div>
              )}

              {mode === "practice" && (
                <div className="pt-1">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-brown border-gray-300 rounded focus:ring-brown"
                      checked={form.hidden}
                      onChange={(e) => setForm((p) => ({ ...p, hidden: e.target.checked }))}
                    />
                    Hidden (not shown in library)
                  </label>
                </div>
              )}

              {mode === "live" && (
                <div className="pt-1">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-brown border-gray-300 rounded focus:ring-brown"
                      checked={form.hidden}
                      onChange={(e) => setForm((p) => ({ ...p, hidden: e.target.checked }))}
                    />
                    Hidden (not shown in library)
                  </label>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2.5 bg-brown text-white rounded-md text-sm font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  {saving ? "Saving..." : editingId ? "Update Video" : "Create Video"}
                </button>
                {editingId && (
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>

        {/* Videos List Section */}
        <div className="order-2 h-full">
          <div
            className="bg-white rounded-lg shadow-sm h-full flex flex-col lg:max-h-[calc(100vh-80px)]"
            style={matchedListMinHeight ? { minHeight: `${matchedListMinHeight}px` } : undefined}
          >
            <div className="p-4 sm:p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">
                    {mode === "practice" ? "Practice Videos" : mode === "constructor" ? "Constructor Pieces" : "Live Recordings"}
                  </h3>
                  <p className="text-sm text-gray-600 mt-0.5">{filteredItems.length} shown / {items.length} total</p>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={visibilityFilter}
                    onChange={(e) => setVisibilityFilter(e.target.value)}
                    className="px-2.5 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 bg-white"
                  >
                    <option value="all">All</option>
                    <option value="hidden">Hidden</option>
                    <option value="visible">Visible</option>
                  </select>
                </div>
              </div>
            </div>

            <div ref={listScrollRef} className="p-4 sm:p-6 flex-1 min-h-0 lg:overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brown mx-auto mb-4"></div>
                    <p className="text-sm text-gray-500">Loading videos...</p>
                  </div>
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="text-center py-12">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                  <p className="mt-2 text-sm font-medium text-gray-900">No videos match this filter</p>
                  <p className="mt-1 text-sm text-gray-500">Try changing All / Hidden / Visible</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredItems.map((item) => (
                    <div
                      key={item._id}
                      className={`border rounded-lg p-4 hover:border-brown hover:shadow-sm transition ${
                        editingId === item._id ? "border-brown bg-brown bg-opacity-5" : "border-gray-200"
                      }`}
                    >
                      <div className="space-y-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                              {item.duration || 0} min
                            </span>
                            {mode === "practice" && (
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  item.contentType === "lecture"
                                    ? "bg-amber-100 text-amber-700"
                                    : "bg-emerald-100 text-emerald-700"
                                }`}
                              >
                                {item.contentType === "lecture" ? "lecture" : "practice"}
                              </span>
                            )}
                            {item.group && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                                {item.group}
                              </span>
                            )}
                            {item.scope && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                                {item.scope}
                              </span>
                            )}
                            {item.isTrial && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                                trial
                              </span>
                            )}
                            {item.hidden && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700">
                                hidden
                              </span>
                            )}
                            {(item.videos?.ru?.default || item.videoURL) && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
                                RU default
                              </span>
                            )}
                            {(item.videos?.ru?.alternative || item.alternativeVideoURL) && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
                                RU alternative
                              </span>
                            )}
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <button
                              type="button"
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition"
                              onClick={() => handleEdit(item)}
                              title="Edit"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                />
                              </svg>
                            </button>
                            <button
                              type="button"
                              className="p-2 text-red-600 hover:bg-red-50 rounded-md transition"
                              onClick={() => handleDelete(item._id)}
                              title="Delete"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                          {getCoverPhoto(item) ? (
                            <div>
                              <button
                                type="button"
                                onClick={() => openPreview(item)}
                                className="group relative block w-full overflow-hidden rounded-md border border-gray-200"
                                title="Preview video"
                              >
                                <img
                                  src={getCoverPhoto(item)}
                                  alt="Cover"
                                  className="h-40 w-full object-contain bg-gray-50 transition group-hover:scale-[1.02]"
                                />
                                <span className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 transition group-hover:opacity-100">
                                  <span className="rounded-full bg-white/90 px-3 py-1 text-xs font-medium text-gray-800">
                                    Play
                                  </span>
                                </span>
                              </button>
                            </div>
                          ) : (
                            (() => {
                              const primaryUrl = getPrimaryUrl(item);
                              const provider = getProvider(primaryUrl);
                              const thumb =
                                provider === "youtube" ? getYoutubeThumbUrl(primaryUrl) : "";

                              if (thumb) {
                                return (
                                  <button
                                    type="button"
                                    onClick={() => openPreview(item)}
                                    className="group relative block w-full overflow-hidden rounded-md border border-gray-200 bg-gray-50 hover:bg-gray-100 transition"
                                    title="Play piece"
                                  >
                                    <img
                                      src={thumb}
                                      alt="YouTube preview"
                                      className="h-40 w-full object-contain bg-gray-50"
                                    />
                                    <span className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 transition group-hover:opacity-100">
                                      <span className="rounded-full bg-white/90 px-3 py-1 text-xs font-medium text-gray-800">
                                        Play
                                      </span>
                                    </span>
                                  </button>
                                );
                              }

                              return (
                                <button
                                  type="button"
                                  onClick={() => openPreview(item)}
                                  className="h-40 w-full rounded-md border border-gray-200 bg-gray-50 hover:bg-gray-100 transition flex items-center justify-center"
                                  title="Play piece"
                                >
                                  <div className="flex items-center gap-2 text-brown font-medium text-sm">
                                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white border border-gray-200">
                                      <svg
                                        className="w-4 h-4"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                        aria-hidden
                                      >
                                        <path d="M6.5 5.5a1 1 0 011.52-.854l7 4.5a1 1 0 010 1.708l-7 4.5A1 1 0 016.5 14.5v-9z" />
                                      </svg>
                                    </span>
                                    Play
                                  </div>
                                </button>
                              );
                            })()
                          )}
                          <div className="min-w-0">
                            {mode === "practice" && (item.header?.ru || item.header?.en) && (
                              <p className="text-sm font-semibold text-gray-800 truncate mb-0.5">
                                {item.header?.ru || item.header?.en}
                              </p>
                            )}
                            {mode === "practice" && (item.description?.ru || item.description?.en) && (
                              <p className="text-xs text-gray-500 line-clamp-2 mb-1.5">
                                {item.description?.ru || item.description?.en}
                              </p>
                            )}
                            <p className="text-xs text-gray-500 font-mono break-all mb-2">{item.uuid}</p>
                            {(item.tags || []).length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {(item.tags || []).map((tag, idx) => (
                                  <span
                                    key={idx}
                                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-50 text-gray-600 border border-gray-200"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {previewItem && (
        <PopupModal onClose={closePreview}>
          <div className="space-y-3">
            <VideoDisplay videoUrl={getActivePreviewUrl()} />
            <div className="mt-3 flex justify-center gap-2">
              <ActionToggleButton
                isActive={!useAlternativePlayer}
                onClick={() => setUseAlternativePlayer(false)}
                activeLabel="YouTube"
                inactiveLabel="YouTube"
                activeIcon=""
                inactiveIcon=""
                variant="flat"
                stylesByVariant={{
                  flat: {
                    active: "bg-pink text-white border-pink",
                    inactive: "bg-white text-brown border-brown hover:bg-lightBeige",
                  },
                  elevated: {
                    active: "bg-pink text-white border-pink",
                    inactive: "bg-white text-brown border-brown hover:bg-lightBeige",
                  },
                }}
                focusRingClass="focus-visible:ring-pink/40"
              />
              <ActionToggleButton
                isActive={useAlternativePlayer}
                onClick={() => setUseAlternativePlayer(true)}
                activeLabel="Alternative"
                inactiveLabel="Alternative"
                activeIcon=""
                inactiveIcon=""
                variant="flat"
                stylesByVariant={{
                  flat: {
                    active: "bg-pink text-white border-pink",
                    inactive: "bg-white text-brown border-brown hover:bg-lightBeige",
                  },
                  elevated: {
                    active: "bg-pink text-white border-pink",
                    inactive: "bg-white text-brown border-brown hover:bg-lightBeige",
                  },
                }}
                focusRingClass="focus-visible:ring-pink/40"
              />
            </div>
          </div>
        </PopupModal>
      )}
    </div>
  );
};

export default AdminVideos;
